import { useMemo } from "react";
import { useSelector } from "react-redux";

// classes
import Aircraft from "classes/aircraft";
import { Staff, WorkerType } from "classes/Staff";

/**
 * @typedef PilotEntry
 * @type {object}
 * @property {Array<string, string>} name - Worker position names [en, pl].
 * @property {string} icon - Font-Awesome icon class.
 * @property {Pilot} worker - The Pilot object.
 */
/**
 * @param {number} aircraftId
 * @returns {Array<Array<PilotEntry>, Array<FlightAttendant>, Array<FlightAttendant>>} [...pilots, purser, ...cabinCrew]
 */
function useAircraftCrew(aircraftId) {
	if (typeof aircraftId !== "number") {
		throw new TypeError(`Expected to get number for aircraftId but got ${typeof aircraftId}`);
	}

	const staff = useSelector(s => s.staff);
	const aircraft = useMemo(() => Aircraft.get(aircraftId), [aircraftId]);
	const requiredPilotCrew = useMemo(() => aircraft.requiredPilotCrew, [aircraft]);
	const requiredCabinCrew = useMemo(() => aircraft.requiredCabinCrew, [aircraft]);

	const pilotCrew = useMemo(
		() =>
			requiredPilotCrew.map(([en, pl, icon], i) => ({
				name: [en, pl],
				icon,
				worker: Staff.getAssignedWorker(WorkerType.PILOT, aircraftId, 5 - i, staff)
			})),
		[aircraftId, requiredPilotCrew, staff]
	);

	const purser = useMemo(() => {
		const assignedWorkers = Staff.getAssignedWorker(WorkerType.FLIGHT_ATTENDANT, aircraftId, 1, staff);
		return new Array(+(requiredCabinCrew > 0)).fill(null).toSpliced(0, assignedWorkers.length, ...assignedWorkers);
	}, [aircraftId, requiredCabinCrew, staff]);

	const cabinCrew = useMemo(() => {
		const assignedWorkers = Staff.getAssignedWorker(WorkerType.FLIGHT_ATTENDANT, aircraftId, 0, staff);
		// console.log("run", assignedWorkers.length);
		return new Array(Math.max(requiredCabinCrew - 1, 0)) // cannot be less than 0
			.fill(null)
			.toSpliced(0, assignedWorkers.length, ...assignedWorkers);
	}, [aircraftId, requiredCabinCrew, staff]);

	return [pilotCrew, cabinCrew, purser];
}

export default useAircraftCrew;
